import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CandidateService } from '../CandidateService';

// Candidate Management mutations
export const useSubmitCandidates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, requirementLocationId, candidates }) =>
      CandidateService.submitCandidates(id, requirementLocationId, candidates),
    onSuccess: (_, { id }) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate specific candidate status queries
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta'],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'list'],
      });
      // Invalidate opportunity details as candidate count may change
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'details', id],
      });
      // Invalidate requirement-locations too — it carries each location's
      // own candidates_submitted count (the badge in AssignmentPipeline's
      // location dropdown, and PipelineOverviewHeader's "of N positions"
      // summary), which otherwise stays stale for up to 5 minutes
      // (staleTime) after submitting from this assignment.
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'requirement-locations'],
      });
    },
  });
};

export const useDeleteCandidates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => CandidateService.deleteCandidates(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate specific candidate status queries
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status', id],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta'],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'secondary'],
      });
      // Invalidate opportunity details as candidate count may change
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'details', id],
      });
    },
  });
};

export const useSubmitSecondaryCandidates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assignmentId, requirementLocationId, candidates }) =>
      CandidateService.submitSecondaryCandidates(
        assignmentId,
        requirementLocationId,
        candidates
      ),
    onSuccess: (_, { assignmentId }) => {
      queryClient.invalidateQueries({ queryKey: ['candidates', 'secondary'] });
      // AssignmentPipeline.jsx's own candidate list (useCandidatesByStatusForTa)
      // is keyed 'by-status-ta', not 'by-status' — the latter was invalidating
      // a query key nothing actually uses, so the Screening Pipeline's table/
      // tab counts never refreshed after a TA submit.
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta', assignmentId],
      });
      // Same gap useSubmitCandidates (CSE) had: requirement-locations carries
      // each location's own candidates_submitted count — the badge in
      // AssignmentPipeline's location dropdown, and PipelineOverviewHeader's
      // "of N positions" summary — which otherwise stays stale for up to 5
      // minutes (staleTime) after submitting from this assignment.
      queryClient.invalidateQueries({
        queryKey: ['opportunity', 'requirement-locations'],
      });
    },
  });
};

// Test-related mutations
export const useUpdateScore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => CandidateService.updateScore(data),
    onSuccess: (_, variables) => {
      // Handle both old and new payload structures
      const candidateId =
        variables.candidate_reference_id || variables.candidateId;
      const opportunityId = variables.requirement_id || variables.opportunityId;

      if (candidateId && opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['candidate', 'scores', candidateId, opportunityId],
        });
      }
    },
  });
};

export const useSendTestLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => CandidateService.sendTestLink(data),
    onSuccess: (_, variables) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate candidate scores query to update link_send_at
      if (variables.candidate_reference_id && variables.requirement_id) {
        queryClient.invalidateQueries({
          queryKey: [
            'candidate',
            'scores',
            variables.candidate_reference_id,
            variables.requirement_id,
          ],
        });
      }
      // Also invalidate by-status queries if opportunityId is available
      if (variables.requirement_id) {
        queryClient.invalidateQueries({
          queryKey: ['candidates', 'by-status', variables.requirement_id],
        });
        queryClient.invalidateQueries({
          queryKey: ['candidates', 'by-status-ta'],
        });
      }
    },
  });
};

export const useAssignTest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => CandidateService.assignTest(data),
    onSuccess: (_, { opportunityId }) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate specific candidate status queries
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status', opportunityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta'],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'list'],
      });
      // Invalidate candidate scores as test assignment affects scores
      queryClient.invalidateQueries({
        queryKey: ['candidate', 'scores'],
      });
    },
  });
};

// Backs GenericFlowAction.jsx — one mutation, any flow/any action key, per
// the generic manual-flow-action endpoint (PR #748's API guide).
export const useExecuteFlowAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ secMappingId, actionKey, payload }) =>
      CandidateService.executeFlowAction(secMappingId, actionKey, payload),
    onSuccess: (_data, variables) => {
      // The action may have moved the candidate to a different status (and
      // therefore a different tab) — invalidate broadly like useAssignTest
      // does, rather than trying to guess which specific queries changed.
      //
      // skipListInvalidate: GenericFlowAction sets this for a save that
      // keeps its modal open (the Details tab — see its deferredSuccessRef
      // comment). Refetching now would land the candidate on a new tab, drop
      // its row, and unmount the still-open modal with it. GenericFlowAction
      // flushes the invalidation itself once the modal actually closes.
      if (variables?.skipListInvalidate) return;
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
};

// Backs UpdateProfileDetailsModal.jsx's Skills tab — separate endpoint from
// interview_profile_update (Details tab), confirmed live: the two panes'
// data no longer travels through one combined flow-action call.
export const useUpdateCandidateSkillMatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ secMappingId, skills }) =>
      CandidateService.updateCandidateSkillMatch(secMappingId, skills),
    onSuccess: (_, { secMappingId }) => {
      queryClient.invalidateQueries({
        queryKey: ['candidate', 'skill-match', secMappingId],
      });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
};

export const useCompleteCandidateScore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, opportunityId }) =>
      CandidateService.completeCandidateScore(candidateId, opportunityId),
    onSuccess: (_, { candidateId, opportunityId }) => {
      queryClient.invalidateQueries({
        queryKey: ['candidate', 'scores', candidateId, opportunityId],
      });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status', opportunityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta', opportunityId],
      });
    },
  });
};

export const useUpdateTestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => CandidateService.updateTestStatus(data),
    onSuccess: (_, variables) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate candidate scores if available
      if (variables.candidate_reference_id && variables.requirement_id) {
        queryClient.invalidateQueries({
          queryKey: [
            'candidate',
            'scores',
            variables.candidate_reference_id,
            variables.requirement_id,
          ],
        });
      }
    },
  });
};

export const useRefreshCandidateSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ candidateId, opportunityId }) =>
      CandidateService.refreshCandidateSession({ candidateId, opportunityId }),
    onSuccess: (_, { candidateId, opportunityId }) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate candidate scores
      if (candidateId && opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['candidate', 'scores', candidateId, opportunityId],
        });
      }
      // Invalidate by-status queries
      if (opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['candidates', 'by-status', opportunityId],
        });
        queryClient.invalidateQueries({
          queryKey: ['candidates', 'by-status-ta'],
        });
      }
    },
  });
};

export const useBulkUpdateFromTASubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ opportunityId, data }) =>
      CandidateService.bulkUpdateFromTASubmission(opportunityId, data),
    onSuccess: (_, { opportunityId }) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status', opportunityId],
      });
      queryClient.invalidateQueries({
        queryKey: ['candidates', 'by-status-ta', opportunityId],
      });
    },
  });
};

// Joining-related mutations
export const useVerifyJoiningDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => CandidateService.verifyJoiningDetails(data),
    onSuccess: (_, variables) => {
      // Invalidate all candidate queries
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Invalidate candidate profile if candidateId is available
      if (variables.candidate_id) {
        queryClient.invalidateQueries({
          queryKey: ['candidate', 'profile', variables.candidate_id],
        });
      }
    },
  });
};

// Constraint-mode mutations (Match Rules save). A mode change can move who's
// dropped/ranked, so both invalidate the constraint-modes read AND the
// ranked candidate list (any page/search/tier) for the same requirement.
export const useSetConstraintMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requirementId, field, mode }) =>
      CandidateService.setRequirementConstraintMode(requirementId, field, mode),
    onSuccess: (_, { requirementId }) => {
      queryClient.invalidateQueries({
        queryKey: ['requirement', 'constraint-modes', requirementId],
      });
      queryClient.invalidateQueries({
        queryKey: ['requirement-matches', requirementId],
      });
    },
  });
};

export const useRemoveConstraintMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requirementId, field }) =>
      CandidateService.removeRequirementConstraintMode(requirementId, field),
    onSuccess: (_, { requirementId }) => {
      queryClient.invalidateQueries({
        queryKey: ['requirement', 'constraint-modes', requirementId],
      });
      queryClient.invalidateQueries({
        queryKey: ['requirement-matches', requirementId],
      });
    },
  });
};
